import {
  INCOME_SOURCE_BADGE_STATUSES_MAP,
  INCOME_STATUSES,
  OCROLUS_FAIL_STATUSES
} from "./constants";
import { IncomeSourceMappedStatus, NetIncomeAnalysis } from "./types";
import { schemeCategory10 } from "d3-scale-chromatic";
import { CombinedIncome, MappedIncome } from "./types/income";
import { Warning } from "./types/verified/common";
import { getIncomeJobName, isIncomeVerificationSkipped } from "./utils";
import { toMoney } from "shared/utils/currency";
import { parseDocStatus } from "views/applications-id-report/utils/parseDocStatus";
import { HugeiconsIcon } from "@hugeicons/react";
import { File02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Badge, BadgeSize, Show } from "shared/components";
import { InvalidData } from "./components/InvalidData";
import {
  IncomeSourceProvider,
  IncomeStatus,
  IncomeType,
  VerifyMethodType
} from "shared/types";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { BankStatementTableRowData } from "./slices/TablesSlice/components/BankStatementTable/types";
import { DateTime } from "luxon";
import {
  getW2InscribeStatus,
  type W2InscribeAdditionalData
} from "./slices/TablesSlice/components/W2Table/utils";

const resolveDocumentStatus = (
  row: Warning & Record<string, unknown>
): "Rejected" | "Warning" | "Accepted" => {
  if (
    row.verifyType === VerifyMethodType.BankStatement ||
    row.verifyType === VerifyMethodType.BenefitsStatement
  )
    return parseDocStatus(
      (row.raw_data as { trust_score?: number })?.trust_score ?? 0
    )!;

  const isInscribeW2Row =
    row.verifyType === VerifyMethodType.W2 &&
    ("income_source_provider" in row
      ? row.income_source_provider === IncomeSourceProvider.Inscribe
      : "additional_data" in row && Boolean(row.additional_data));

  if (isInscribeW2Row)
    return getW2InscribeStatus(
      ("additional_data" in row ? row.additional_data : undefined) as
        | W2InscribeAdditionalData
        | null
        | undefined,
      "source_status" in row
        ? (row.source_status as string | undefined)
        : undefined
    );

  const formAnalysis =
    (row.doc_analysis as { form_analysis?: Warning["form_analysis"] })
      ?.form_analysis ?? row.form_analysis;
  const hasFormAnalysis = Boolean(formAnalysis?.length);
  const isWarningEntity = "doc_info" in row;

  if (!hasFormAnalysis && !isWarningEntity) return "Accepted";

  const isInvalidDoc = OCROLUS_FAIL_STATUSES.includes(
    (row.status as string | undefined) ?? row.detect_status
  );
  return parseDocStatus(
    isInvalidDoc ? 0 : formAnalysis?.[0]?.form_authenticity?.score || 0
  )!;
};

export const defaultTableColumns = [
  {
    header: "Verified income sources",
    accessor: (row) => row.source,
    widthClass: "w-full"
  },
  {
    header: "Date range",
    accessor: (row) => row.dateRange,
    widthClass: "w-full"
  },
  {
    header: "Gross monthly",
    accessor: (row) => row.grossMonthly,
    widthClass: "w-[85px] shrink-0"
  },
  {
    header: "Net monthly",
    accessor: (row) => row.netMonthly,
    widthClass: "w-[85px] shrink-0"
  }
];

const documentColumn = {
  header: "Document",
  accessor: (row, index) => {
    return (
      <Tooltip>
        <TooltipTrigger>
          <div className="flex items-center gap-2">
            <HugeiconsIcon
              icon={File02Icon}
              size={20}
              stroke="1px"
              className="text-content-100"
            />
            <p className="text-content-1000 font-medium text-sm w-[120px] text-ellipsis whitespace-nowrap overflow-hidden">
              {row.asset?.asset?.filename ?? `Document #${index + 1}`}
            </p>
          </div>
        </TooltipTrigger>
        <TooltipContent className="max-w-[370px] rounded-lg z-[50] border bg-alpha-800 p-4 text-sm text-content-1000-inverted">
          {row.asset?.asset?.filename ?? `Document #${index + 1}`}
        </TooltipContent>
      </Tooltip>
    );
  },
  widthClass: "w-full"
};

const dateRangeColumn = {
  header: "Date range",
  accessor: (row) => {
    let startDate: string;
    let endDate: string;

    if (row?.verifyType === VerifyMethodType.BankStatement) {
      startDate = row?.additional_data?.bank_statement_start_date;
      endDate = row?.additional_data?.bank_statement_end_date;
    } else {
      startDate = row?.paystub_details?.pay_period_start_date;
      endDate = row?.paystub_details?.pay_period_end_date;
    }

    return (
      <p className="text-content-200 font-medium text-sm">
        {!startDate && !endDate ? (
          "-"
        ) : (
          <>
            {startDate ? (
              DateTime.fromISO(startDate).toFormat("MMM d, yyyy")
            ) : (
              <InvalidData text="Invalid start date" />
            )}{" "}
            -{" "}
            {endDate ? (
              DateTime.fromISO(endDate).toFormat("MMM d, yyyy")
            ) : (
              <InvalidData text="Invalid end date" />
            )}
          </>
        )}
      </p>
    );
  },
  widthClass: "w-full"
};

const yearColumn = {
  header: "Year",
  accessor: (row) => (
    <p className="text-content-200 font-medium text-sm">
      {row?.year ? String(row.year) : "-"}
    </p>
  ),
  widthClass: "w-full"
};

const grossPayColumn = {
  header: "Gross pay",
  accessor: (row) => {
    if (row?.verifyType === VerifyMethodType.BankStatement) {
      const { totals, incomeId } = row as BankStatementTableRowData;
      const { gross_total } = (incomeId && totals?.[incomeId]) || {};

      return gross_total ? toMoney(gross_total) : "-";
    }

    return row?.total_gross_pay_amount
      ? toMoney(row?.total_gross_pay_amount)
      : "-";
  },
  widthClass: "w-[85px] shrink-0 text-right"
};

const netPayColumn = {
  header: "Net pay",
  accessor: (row) => {
    if (row?.verifyType === VerifyMethodType.BankStatement) {
      const { totals, incomeId } = row as BankStatementTableRowData;
      const { net_total } = (incomeId && totals?.[incomeId]) || {};

      return net_total ? toMoney(net_total) : "-";
    }

    return row.total_net_pay_amount ? toMoney(row.total_net_pay_amount) : "-";
  },
  widthClass: "w-[85px] shrink-0 text-right"
};

const statusColumn = {
  header: "Status",
  accessor: (row) => {
    const status = resolveDocumentStatus(row);

    const config = INCOME_SOURCE_BADGE_STATUSES_MAP[status];

    if (config)
      return (
        <Badge
          variant={config.variant}
          size={BadgeSize.SM}
          className="items-center"
        >
          {config.title}
          <Show if={status === "Rejected"}>
            {getRejectionReason(row).title}
          </Show>
        </Badge>
      );

    return null;
  },
  widthClass: "w-full text-right"
};

export const documentTypeTableColumns = [
  documentColumn,
  dateRangeColumn,
  grossPayColumn,
  netPayColumn,
  statusColumn
];

export const w2TableColumns = [
  documentColumn,
  yearColumn,
  grossPayColumn,
  netPayColumn,
  statusColumn
];

export const documentWithoutDatesTableColumns = [
  documentColumn,
  grossPayColumn,
  netPayColumn,
  statusColumn
];

export function formatMonths(months: number): string {
  if (months < 12) return `${months} month${months === 1 ? "" : "s"}`;

  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;
  const parts: string[] = [];

  if (years) parts.push(`${years} year${years > 1 ? "s" : ""}`);
  if (remainingMonths)
    parts.push(`${remainingMonths} month${remainingMonths > 1 ? "s" : ""}`);

  return parts.join(", ");
}

export function mapNetIncomeAnalysis(data: NetIncomeAnalysis): MappedIncome[] {
  const monthMap = new Map<string, MappedIncome>();
  const companies = Object.keys(data);
  const colorMap = Object.fromEntries(
    companies.map((company, index) => [
      company,
      schemeCategory10[index % schemeCategory10.length]
    ])
  );

  Object.entries(data).forEach(([company, months]) => {
    Object.entries(months).forEach(([month, incomeData]) => {
      let income = 0;

      if (!monthMap.has(month))
        monthMap.set(month, {
          month,
          total: 0,
          income: []
        });

      const entry = monthMap.get(month)!;

      if (typeof incomeData === "object" && "total" in incomeData) {
        income = incomeData.total;
        incomeData.sources.forEach((source) => {
          entry.income.push({
            month: source.pay_date,
            company: source.description,
            income: source.net_amount,
            color: colorMap[company]
          });
        });
      } else {
        income = incomeData;

        entry.income.push({
          month,
          company,
          income,
          color: colorMap[company]
        });
      }

      entry.total += income;
    });
  });

  return Array.from(monthMap.values()).sort((a, b) =>
    b.month.localeCompare(a.month)
  );
}

export const scrollToIncomeSource = (incomeId: number) => {
  const element = document.getElementById(
    `combined-income-section-${incomeId}`
  );
  if (element) element.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const mapIncomeSourceStatus = (
  income: CombinedIncome
): IncomeSourceMappedStatus => {
  const incomeStatusMeta = {
    incomeId: income.id,
    incomeName: getIncomeJobName(income.additional_data),
    type: income.type
  };

  if (income.type === IncomeType.OfferLetter)
    return {
      ...incomeStatusMeta,
      description: `"${incomeStatusMeta.incomeName}" was not successfully verified because offer letters require manual review`,
      status: "Rejected"
    };

  if (isIncomeVerificationSkipped(income))
    return {
      ...incomeStatusMeta,
      description: "Income verification was skipped",
      status: "Rejected"
    };

  if ([IncomeStatus.Pending, IncomeStatus.Processing].includes(income.status))
    return {
      ...incomeStatusMeta,
      description: "",
      status: "Started"
    };

  if (income.status === IncomeStatus.FinishedByOverride)
    return {
      ...incomeStatusMeta,
      description: "Verification manually marked as complete",
      status: "Overridden"
    };

  let rejectedDocumentsCount = 0;
  let documentsStatus: "Rejected" | "Warning" | "Accepted" | undefined;
  let description = `"${incomeStatusMeta.incomeName}"`;
  const warningMessage =
    " was not successfully verified because all documents were rejected";
  const rejectedMessage = " has verification warnings on one or more documents";

  if (income.verified_source_type === VerifyMethodType.BenefitsStatement) {
    // If it's missing verified amounts, mark as rejected - otherwise use trust_score
    if (
      !income.verified_monthly_amounts.gross ||
      !income.verified_monthly_amounts.net
    )
      documentsStatus = "Rejected";
    else {
      const trustScore =
        income.items.at(0)?.income_source_data?.raw_data?.trust_score;
      documentsStatus = parseDocStatus(trustScore ?? 0);
    }

    if (documentsStatus === "Accepted") description = "";
    if (documentsStatus === "Warning") description += warningMessage;
    if (documentsStatus === "Rejected") description === rejectedMessage;

    return {
      ...incomeStatusMeta,
      status: documentsStatus || INCOME_STATUSES[income.status],
      description
    };
  }

  income.items.forEach((item) => {
    if (
      (item.income_source_type === "paystub" ||
        item.income_source_type === "w2") &&
      item.data?.document?.warnings?.length
    ) {
      item.data.document.warnings.forEach((warning: Warning) => {
        const isDocumentRejected =
          OCROLUS_FAIL_STATUSES.includes(warning.detect_status) ||
          parseDocStatus(
            warning.form_analysis?.[0]?.form_authenticity?.score ?? 0
          ) === "Rejected";

        if (isDocumentRejected) rejectedDocumentsCount += 1;
      });

      // If every item is rejected - the income is considered to be Rejected, otherwise - it's a Warning
      if (
        !item.data.document.income_sources?.length &&
        item.data.document.warnings?.length === rejectedDocumentsCount
      ) {
        documentsStatus = "Rejected";
        description += rejectedMessage;
      } else {
        documentsStatus = "Warning";
        description += warningMessage;
      }
    }
    // No item warnings - income is Accepted
    else {
      documentsStatus = "Accepted";
      description = "";
    }
  });

  return {
    ...incomeStatusMeta,
    status: documentsStatus || INCOME_STATUSES[income.status],
    description
  };
};

export const mapUnverifiedIncomeStatus = (income: CombinedIncome) => {
  const incomeName = getIncomeJobName(income.additional_data);

  return {
    incomeId: income.id,
    incomeName,
    description: `${incomeName} was not successfully verified because one or more documents were rejected`,
    status: "Rejected"
  };
};

const getRejectionReason = (warning: Warning) => {
  const hasFormAnalysis = Boolean(warning.form_analysis?.length);
  const isWarningEntity = "doc_info" in warning;
  const isInvalidDocType =
    OCROLUS_FAIL_STATUSES.includes(warning.detect_status) ||
    (isWarningEntity && !hasFormAnalysis);
  const isLowScore =
    parseDocStatus(
      isInvalidDocType
        ? 0
        : (warning.form_analysis?.[0]?.form_authenticity?.score ?? 0)
    ) === "Rejected";
  const verboseReason =
    warning.doc_info?.rejection_reason_description ||
    warning.doc_info?.rejection_reason ||
    "Unknown error";

  if (isInvalidDocType)
    return {
      title: " - Invalid document",
      verboseReason
    };
  else if (isLowScore)
    return {
      title: " - Potential fraud",
      verboseReason
    };
  else
    return {
      title: "",
      verboseReason
    };
};
